/**
 * KP signed-significator scoring engine — the deterministic event-timing half of the
 * birth-time ranking.
 *
 * For each dated event it computes every graha's signed KP significator strength (grade for
 * the event's positive houses minus its contra houses) and rewards birth times where the
 * event-date dasha chain, the activated cusp sub-lord, and the Ascendant sub-lord lean toward
 * the positive significators. See PLAN.md and RULES_TRACE.md for the sourced methodology.
 */

import { local_to_jd } from "../astro/chart";
import { SIGN_NAMES, SIGN_RULERS } from "../astro/constants";
import { get_current_dasha, init_dasha_jd } from "../astro/dasha";
import { kp_sublord_chain, net_significator_strength, ruling_planets } from "../astro/kp";
import { dasha_lord_at_level } from "../astro/models";
import type { RawChartData } from "../astro/models";
import { weighted_mean } from "./event_model";
import { category_division, score_divisional_for_event } from "./divisional_scoring";
import { score_transit_for_event, _aspected_signs } from "./transit_scoring";
import { score_yoga_for_event } from "./yoga_scoring";
import {
  sunrise_anchor, special_lagnas, score_ghati_lagna_for_event, score_hora_lagna_for_event,
  GHATI_LAGNA_CATEGORIES, HORA_LAGNA_CATEGORIES,
} from "./special_points";
import {
  _cached_event_chart,
  _event_datetime_range,
  event_midpoint_datetime,
  event_precision_weight_multiplier,
  optional_float,
} from "./scoring_common";
import { gate_lord_score, graha_strength_factor } from "./strength_scoring";
import { dasha_lord_sav_multiplier, event_house_sav_multiplier } from "./ashtakavarga";
import { event_offset_at } from "./timezones";
import { influence_factor } from "./traits";
import type { PlainDateTime } from "./datetime";
import { compareDates, dateOnly, diffSeconds, todayLocal } from "./datetime";
import { pymod, pythonRound } from "./pyutil";

// KP house GROUPS each event is judged from — the standard "houses to be judged"
// in Krishnamurti Paddhati. An event fructifies in the dasha of significators of the
// WHOLE group, not a single house: marriage from 2-7-11 (family, spouse, fulfilment),
// children from 2-5-11, etc. Judging from one house alone yields too few significators
// and wrongly scores the true birth time low. The topically primary house is listed
// first (used for the cuspal sub-lord test).
export const CATEGORY_PRIMARY_HOUSES: Record<string, number[]> = {
  marriage: [7, 2, 11],
  intimacy_relationship: [7, 5, 11],
  relationship_conflict: [7, 6, 12],
  child_birth: [5, 2, 11],
  // Education: 4th (foundational education/schooling) primary, with the 5th (intelligence,
  // learning, Purva Punya — 04_houses_1_to_6_significations.md) and 9th (higher education).
  education_start: [4, 5, 9],
  career_learning: [4, 9, 10],
  career_breakthrough: [10, 6, 11],
  career_contract: [10, 6, 11],
  career_failure: [10, 8, 12],
  award: [10, 11, 6],
  // Death of a family member: 2 & 7 (maraka houses), 8 (death/longevity backing). The 3rd is the
  // only house the sources EXPLICITLY name for a relative's death — 04_houses_1_to_6_significations.md
  // H3: "death of parents (as 3rd is 12th from the 4th)" — but the SAME source conditions it:
  // "The 3rd as 'death of parents' is a derived house relationship, confirmed only when the 4th
  // house is also afflicted." So the 3rd is added CONDITIONALLY at scoring time (see
  // death_family_houses / fourth_house_afflicted below), not unconditionally here.
  death_family: [2, 7, 8],
  injury: [6, 8, 12],
  anxiety_health: [6, 8, 12],
  mood_low: [8, 12, 6],
  pet_death: [6, 8, 12],
  // Buying/registering property (land, a house, a flat — an ASSET acquisition, distinct from
  // moving residence): the 4th (immovable property/land/fixed assets) is primary — NOT the 9th
  // (fortune): real estate is classically a 4th-house matter — with the 11th (gain/acquisition)
  // and 2nd (accumulated wealth/assets).
  property_purchase: [4, 11, 2],
  // Change of residence (where you live), distinct from buying property: the 4th (home/residence)
  // is primary, with the 3rd (short journeys/movement) and 12th (foreign/distant residence —
  // 04_houses_7_to_12_significations.md: 12th = "foreign residence" primary).
  relocation: [4, 3, 12],
  travel_family: [3, 9, 12],
  adventure_positive: [3, 9, 12],
  social_celebration: [11, 5, 2],
};

// Levels run maha → prana. Per the docs the Mahadasha lord dominates (sets the theme
// and must not be subordinated), while the Antardasha activates the specific event and
// the Pratyantardasha pinpoints it (Vol 8 timing protocol). So maha and antar are
// co-equal (maha is never weighted below antar) and the finer levels add birth-time
// resolution because their boundaries shift by the second.
export const DASHA_LEVEL_WEIGHTS: Record<string, number> = {
  maha: 1.0,
  antar: 1.0,
  pratyantar: 0.6,
  sukshma: 0.3,
  prana: 0.15,
};

// Narrow-window fine re-weighting (measured problem, 2026-07-20): at second resolution only the
// prana/sukshma dasha boundaries, the D60 lagna (~2 min) and the Ruling-Planet sub-lord actually
// MOVE — yet prana carried ~5% of the dasha component and <2% of the score, so drill-down ranks
// plateaued. The boost keys on its OWN threshold (`fine_boost` from the caller, span ≤
// FINE_BOOST_MAX_SPAN_SECONDS), NOT on the ~2.5h include_fine_divisional regime: re-weighting a
// whole rising-sign window overweights components that are noise at that resolution (measured: it
// flipped the Raahil Leo-vs-Virgo per-sign holdout pin), while inside a ≤15-min drill-down the
// second-sensitive shares are exactly the ones that discriminate. Boosted shares renormalise via
// the weighted means, so the total stays comparable; every wider path is byte-identical.
export const FINE_REGIME_BOOST = 2.0;
export const FINE_BOOST_MAX_SPAN_SECONDS = 900;
// The dasha levels whose boundaries shift by the second — the ones the fine regime boosts.
const _FINE_SENSITIVE_LEVELS = new Set(["sukshma", "prana"]);

// ── Dasha-Chidra + Decanate timing (source_learning/08_dasha_fundamentals.md) ────────────────
// "Dasha Chidra — Junction Between Two Mahadashas": "The final months of one Mahadasha and the
// opening months of the next … Frequently marked by significant life events, transitions, deaths
// of relatives, or health challenges. Timing: Approximately 3–6 months on either side of the
// Dasha transition point." Mahadasha junction dates shift by DAYS per minute of birth time (the
// balance at birth scales the whole chain), so a sharply-dated transition event landing inside a
// candidate's chidra window is a genuine minutes-level discriminator. Applied as a bounded,
// never-penalising ADDITIVE bonus on the dasha component (PLAN §6 / RULES_TRACE "How to extend"
// pattern): closer to the junction = larger, zero outside the window, never subtracts.

/**
 * Transition-type categories eligible for the chidra bonus: the doc names "significant life
 * events, transitions, deaths of relatives" — restricted to categories whose
 * CATEGORY_TIMING_RELIABILITY is ≥ 0.7 (sharply datable), so a fuzzy "we moved that year"
 * (relocation 0.6) or a loosely-dated illness (anxiety_health 0.4) cannot fabricate junction hits.
 */
export const CHIDRA_CATEGORIES: Set<string> = new Set([
  "marriage", "career_breakthrough", "career_contract", "career_failure", "death_family",
]);
// "Approximately 3–6 months on either side" — the outer 6-month bound, graded to zero at the edge.
export const CHIDRA_MAHA_WINDOW_DAYS = 183.0;
export const CHIDRA_MAHA_MAX_BONUS = 6.0;
// Antardasha junctions mark the same review/orientation dynamic at the sub-period scale; the
// window is scaled down (antars run months-to-years, so a 6-month orb would saturate) and the
// bonus halved — the maha junction remains the classically-stated chidra.
export const CHIDRA_ANTAR_WINDOW_DAYS = 45.0;
export const CHIDRA_ANTAR_MAX_BONUS = 3.0;

// "Decanate Rule — Timing Within a Dasha" (08_dasha_fundamentals.md): dasha lord in the
// 1st Decanate (0°–10°) → effects at the COMMENCEMENT of the period; 2nd (10°–20°) → the middle;
// 3rd (20°–30°) → the end. Exceptions (same section): "If Dasha lord is retrograde, timing is
// reversed — 3rd Decanate effects come first"; "Rahu and Ketu are always retrograde; their Dasha
// effects always manifest in reverse order." A matching third earns this bonus; a non-matching
// third stays exactly neutral (never subtracts).
export const DECANATE_MATCH_BONUS = 4.0;

// Rahu/Ketu are permanently retrograde (08_dasha_fundamentals.md Decanate Rule exceptions).
const _ALWAYS_RETROGRADE = new Set(["rahu", "ketu"]);

/**
 * Bounded additive timing bonuses for the dasha component (0 .. CHIDRA_MAHA_MAX_BONUS +
 * DECANATE_MATCH_BONUS; never negative):
 *  (a) CHIDRA — for transition-type events (CHIDRA_CATEGORIES), a graded bonus when the event
 *      date falls within the chidra window of a mahadasha (±6 months) or antardasha (±45 days)
 *      junction under THIS candidate. Only junctions the native actually lived through count —
 *      the birth mahadasha's virtual pre-birth start is excluded.
 *  (b) DECANATE — the maha lord's natal decanate predicts the third of its period that delivers
 *      (reversed for retrograde lords and always for Rahu/Ketu); the event landing in the
 *      predicted third earns DECANATE_MATCH_BONUS, any other third exactly 0.
 * Returns [bonus, detail] — detail is null only when the dasha chain itself fails.
 */
export function dasha_timing_bonuses(
  event_dt: PlainDateTime,
  birth_dt: PlainDateTime,
  dasha_info_init: Record<string, any>,
  timezone_offset: number,
  natal_chart: RawChartData,
  category: string,
): [number, Record<string, any> | null] {
  let dasha;
  try {
    dasha = get_current_dasha(event_dt, birth_dt, dasha_info_init, timezone_offset);
  } catch {
    return [0.0, null];
  }
  const event_jd = local_to_jd(event_dt, timezone_offset);
  const birth_jd = Number(dasha_info_init["birth_jd"]);
  const detail: Record<string, any> = {};
  let bonus = 0.0;

  // (a) CHIDRA — junction proximity, graded linearly to zero at the window edge.
  if (CHIDRA_CATEGORIES.has(String(category || "").trim().toLowerCase())) {
    const graded = (junctions: number[], window: number, max_bonus: number): [number, number | null] => {
      // A junction is only real if the native lived through it (post-birth): the first maha/antar
      // start_jd is a virtual pre-birth anchor, not a lived transition.
      const lived = junctions.filter((j) => j > birth_jd + 1e-9);
      if (!lived.length) return [0.0, null];
      const dist = Math.min(...lived.map((j) => Math.abs(event_jd - j)));
      if (dist > window) return [0.0, dist];
      return [max_bonus * (1.0 - dist / window), dist];
    };
    const [maha_bonus, maha_dist] = graded(
      [dasha.maha_start_jd, dasha.maha_end_jd], CHIDRA_MAHA_WINDOW_DAYS, CHIDRA_MAHA_MAX_BONUS,
    );
    const [antar_bonus, antar_dist] = graded(
      [dasha.antar_start_jd, dasha.antar_end_jd], CHIDRA_ANTAR_WINDOW_DAYS, CHIDRA_ANTAR_MAX_BONUS,
    );
    // Max, not sum: one junction hit is one confirmation (a maha junction is also an antar one).
    const chidra = Math.max(maha_bonus, antar_bonus);
    bonus += chidra;
    detail["chidra"] = {
      bonus: pythonRound(chidra, 2),
      maha_junction_days: maha_dist === null ? null : pythonRound(maha_dist, 1),
      antar_junction_days: antar_dist === null ? null : pythonRound(antar_dist, 1),
    };
  }

  // (b) DECANATE — predicted vs actual third of the running mahadasha.
  const lord = dasha.mahadasha_lord;
  const pos = lord ? natal_chart.planets[lord] : undefined;
  const span = dasha.maha_end_jd - dasha.maha_start_jd;
  if (pos !== undefined && span > 0) {
    const decanate = Math.min(2, Math.trunc(pymod(Number(pos.longitude), 30.0) / 10.0));
    const reversed = _ALWAYS_RETROGRADE.has(lord) || Boolean(pos.is_retrograde);
    const predicted_third = reversed ? 2 - decanate : decanate;
    const frac = Math.max(0.0, Math.min(1.0, (event_jd - dasha.maha_start_jd) / span));
    const event_third = Math.min(2, Math.trunc(frac * 3.0));
    const match = event_third === predicted_third;
    if (match) bonus += DECANATE_MATCH_BONUS;
    detail["decanate"] = {
      lord,
      decanate: decanate + 1, // 1st/2nd/3rd drekkana of the lord's sign
      reversed,
      predicted_third: predicted_third + 1, // 1 = start, 2 = middle, 3 = end of the period
      event_third: event_third + 1,
      match,
      bonus: match ? DECANATE_MATCH_BONUS : 0.0,
    };
  }

  return [bonus, Object.keys(detail).length ? detail : null];
}

// Natural significators (karakas) per Brihat Jataka / Vol 4. The karaka is an explicit
// doc-named house significator, so it joins the chart-derived (lord + occupant) set.
// Kept to 1-4 planets per topic so the significator set stays discriminating.
export const CATEGORY_KARAKAS: Record<string, string[]> = {
  marriage: ["venus"], intimacy_relationship: ["venus"], relationship_conflict: ["venus"],
  child_birth: ["jupiter"],
  // Mercury (intellect/learning) leads; Jupiter (wisdom/higher education) second.
  education_start: ["mercury", "jupiter"], career_learning: ["mercury", "jupiter"],
  // Career karakas per 04_houses_7_to_12_significations.md H10: "Natural Karakas: Mercury, Sun,
  // Jupiter, Saturn (all four govern different aspects of career)" — all four are kept.
  career_breakthrough: ["sun", "saturn", "mercury", "jupiter"],
  career_contract: ["sun", "saturn", "mercury", "jupiter"],
  career_failure: ["sun", "saturn", "mercury", "jupiter"], award: ["sun"],
  death_family: ["saturn"], injury: ["mars", "saturn"], anxiety_health: ["saturn", "moon"],
  mood_low: ["moon", "saturn"],
  // Pets/small animals are a 6th-house matter whose natural karaka is MARS
  // (04_houses_1_to_6_significations.md H6: "Natural Karaka: Mars"); KETU explicitly governs
  // animals ("Animals, particularly dogs — either association with or difficulties involving
  // animals" — 02_rahu_ketu_rules.md). Saturn was unsourced here and is dropped.
  pet_death: ["mars", "ketu"],
  // Mars = Bhumi karaka (land/immovable property); Venus = conveyances/comforts/luxuries.
  property_purchase: ["mars", "venus"],
  // Residence change: Moon (home/4th karaka, changes, travel) + Rahu (foreign residence/
  // foreign-wandering — 04_houses_7_to_12_significations.md, 02_rahu_ketu_rules.md).
  relocation: ["moon", "rahu"],
  travel_family: ["moon"], adventure_positive: ["mars"],
  // Money/gains (the era "money" theme routes here): JUPITER is the primary wealth karaka —
  // 04_house_analysis_foundations.md "Natural Karakas of Each House": 2nd = Jupiter,
  // 11th = Jupiter. Venus stays as the secondary luxury/comforts-wealth backing.
  social_celebration: ["jupiter", "venus"],
};

// Contra houses per category: the bhavas whose activation CONTRADICTS the event. KP
// rectification keys on the relevant cusp/dasha lord signifying the positive houses
// (CATEGORY_PRIMARY_HOUSES) more than these. Subtracting them is what breaks
// significator saturation and gives the engine birth-time discrimination.
export const CATEGORY_CONTRA_HOUSES: Record<string, number[]> = {
  marriage: [1, 6, 10],
  intimacy_relationship: [1, 6, 10],
  relationship_conflict: [2, 5, 11],
  child_birth: [1, 4, 10],
  education_start: [3, 8, 12],
  career_learning: [3, 8, 12],
  career_breakthrough: [5, 9, 12],
  career_contract: [5, 9, 12],
  career_failure: [2, 6, 11],
  award: [5, 9, 12],
  death_family: [1, 5, 11],
  injury: [1, 5, 11],
  anxiety_health: [1, 5, 11],
  mood_low: [1, 5, 11],
  pet_death: [1, 5, 11],
  // A benefic asset/home gain is contradicted by the dusthana triad (loss/debt/upheaval).
  property_purchase: [6, 8, 12],
  // The 12th is a PRIMARY relocation house (foreign/distant residence) — it must not also be
  // subtracted here or the doc's foreign-residence signal nets to zero (fixed 2026-07-02).
  relocation: [6, 8],
  travel_family: [1, 4, 8],
  // Mirrors travel_family: staying put (1st), home comfort (4th), obstruction (8th). The 12th is
  // PRIMARY for distant travel and must not be contra (fixed 2026-07-02).
  adventure_positive: [1, 4, 8],
  social_celebration: [6, 8, 12],
};

/**
 * True when an event carries an explicit NEGATIVE signal: an era answer's valence, or a dated
 * event's validated `polarity` ("negative" string from intake, or a numeric < 0 from the legacy
 * LifeEvent encoding). Wiring polarity here is what lets a saved "hard marriage" event judge the
 * contra houses exactly like a hard era answer does.
 */
function _is_negative(event: Record<string, any>): boolean {
  if (String(event["valence"] || "").trim().toLowerCase() === "negative") return true;
  const polarity = event["polarity"];
  if (typeof polarity === "number") return polarity < 0;
  return String(polarity || "").trim().toLowerCase() === "negative";
}

// Affliction-framed categories: their PRIMARY judging houses (anxiety_health 6/8/12,
// death_family 2/7/8) ALREADY encode the hard outcome, so a negative signal must NOT invert
// them — the effective flip is (negative signal) XOR (category negative-natured). "Loss era,
// hard" therefore scores the death houses themselves; only an explicit POSITIVE era valence
// ("the loss/grief area went well" — rare) flips such a category to its contra set.
export const NEGATIVE_NATURED_CATEGORIES: Set<string> = new Set(["anxiety_health", "death_family"]);

/**
 * Whether the event judges its category's CONTRA set instead of the primary set.
 * Effective flip = (negative signal) XOR (category negative-natured), with one guard: the
 * intake's DEFAULT polarity "positive" on a dated event is not an assertion, so only an
 * explicit era VALENCE (era_pseudo_events always writes one) can flip a negative-natured
 * category — a plain dated death/health event keeps its primary affliction houses.
 */
function _houses_flipped(event: Record<string, any>): boolean {
  const negative = _is_negative(event);
  if (!NEGATIVE_NATURED_CATEGORIES.has(String(event["category"] || "").trim().toLowerCase())) {
    return negative;
  }
  if (negative) return false; // XOR: the hard outcome IS the primary house set
  return String(event["valence"] || "").trim().toLowerCase() === "positive";
}

/**
 * The bhavas that contradict an event's category (empty if unknown).
 * For a flipped event (see _houses_flipped) the polarity swaps: a hard period in an area means
 * the period activated the area's CONTRA houses, so its PRIMARY houses become the contradicting set.
 */
export function event_contra_houses(event: Record<string, any>): number[] {
  const category = String(event["category"] || "").trim().toLowerCase();
  const houses = _houses_flipped(event) ? CATEGORY_PRIMARY_HOUSES[category] : CATEGORY_CONTRA_HOUSES[category];
  return [...(houses ?? [])];
}

// How sharply each category times under Vimshottari dasha. Marriage, childbirth and
// death are the classic sharply-datable events; accidents/moods/social events time
// loosely (and their dates are often approximate), so they corroborate rather than
// drive. Used as a multiplier on event weight so a fuzzy event cannot overrule the
// sharp ones.
export const CATEGORY_TIMING_RELIABILITY: Record<string, number> = {
  marriage: 1.0, child_birth: 1.0, death_family: 0.9,
  career_breakthrough: 0.8, career_contract: 0.8, award: 0.75,
  career_failure: 0.7, education_start: 0.7, career_learning: 0.6,
  // A property registration carries an EXACT legal/documented date — sharply datable, so it
  // ranks well above the fuzzy "we moved sometime that year" of a relocation.
  property_purchase: 0.8,
  relocation: 0.6,
  intimacy_relationship: 0.6, relationship_conflict: 0.5,
  injury: 0.5, anxiety_health: 0.4, mood_low: 0.4, pet_death: 0.4,
  travel_family: 0.4, adventure_positive: 0.4, social_celebration: 0.4,
};

// Event category -> coarse life area, used by the holdout-validation breadth check.
// (Python home: lib/holdout_validation.py — that module is not otherwise ported, so the
// table lives here with the other category wiring; test_category_coverage enforces it.)
export const CATEGORY_LIFE_AREA: Record<string, string> = {
  marriage: "relationships", intimacy_relationship: "relationships",
  relationship_conflict: "relationships",
  child_birth: "children",
  education_start: "education", career_learning: "career", career_breakthrough: "career",
  career_contract: "career", career_failure: "career", award: "career",
  death_family: "family", travel_family: "family", social_celebration: "social",
  injury: "health", anxiety_health: "health", mood_low: "health", pet_death: "health",
  property_purchase: "home", relocation: "home", adventure_positive: "travel",
};

// Natural malefics per 08_dasha_quality_assessment.md ("Conjunction with natural malefic
// (Saturn, Mars, Rahu, Ketu): Damages the Dasha") — the set used for the affliction test below.
const _NATURAL_MALEFICS = ["saturn", "mars", "rahu", "ketu"] as const;

/**
 * True when the whole-sign 4th house (from the candidate Ascendant) is AFFLICTED: a natural
 * malefic occupies it or casts its graha drishti on it (Saturn 3/7/10, Mars 4/7/8, others the
 * 7th — the same whole-sign aspect table the transit module uses).
 *
 * Purpose: gate for the death_family 3rd house. 04_houses_1_to_6_significations.md names the
 * 3rd for a relative's death ("death of parents (as 3rd is 12th from the 4th)") but explicitly
 * conditions it: "The 3rd as 'death of parents' is a derived house relationship, confirmed only
 * when the 4th house is also afflicted." Ascendant-dependent, so it also discriminates candidates.
 */
export function fourth_house_afflicted(natal_chart: RawChartData): boolean {
  const asc_sign = Math.trunc(Math.floor(pymod(natal_chart.ascendant_longitude, 360.0) / 30));
  const fourth_sign = pymod(asc_sign + 3, 12);
  for (const malefic of _NATURAL_MALEFICS) {
    const pos = natal_chart.planets[malefic];
    if (pos === undefined) continue;
    const sign = Math.trunc(Math.floor(pymod(pos.longitude, 360.0) / 30));
    if (sign === fourth_sign || _aspected_signs(malefic, sign).has(fourth_sign)) return true;
  }
  return false;
}

/**
 * The death_family judging houses for a specific chart: the table's 2/7/8 (maraka + 8th),
 * PLUS the 3rd exactly when the source's condition holds — the 4th house is afflicted
 * (04_houses_1_to_6_significations.md H3 + its Exception; see fourth_house_afflicted).
 */
export function death_family_houses(natal_chart: RawChartData): number[] {
  const houses = [...CATEGORY_PRIMARY_HOUSES["death_family"]];
  if (fourth_house_afflicted(natal_chart)) houses.push(3);
  return houses;
}

// ── 22nd (Khara) Drekkana — death significator (source_learning/10_maraka_planets.md) ────────
// "Tertiary / Special Marakas": "Lord of the 22nd Drekkana from the Ascendant's Drekkana (Khara
// Drekkana)"; Exceptions: "22nd Drekkana lord is effective across all life classes" — so unlike
// the life-class nakshatra marakas it needs no longevity classification and can join every
// death_family judgement. Counted inclusively, the 22nd drekkana from the Ascendant's is
// (asc_drekkana_index + 21) mod 36 — the drekkana of the 8th-house region — and its lord is the
// ruler of that drekkana's sign under the standard Parashari scheme (parts of a sign map to
// sign, sign+4, sign+8). The drekkana flips every ~40 minutes of birth time, so this is an
// INTRA-sign discriminator. Engine-side only (RULES_TRACE "not yet" scoped the body-mark map to
// a capture surface; the maraka significator itself needs none).

// Partial grade: the khara lord joins the significator set at HALF the doc-named-karaka bonus —
// it corroborates the chart-derived maraka significators, it does not equal Saturn's karaka rank.
export const KHARA_DREKKANA_NET_BONUS = 1.0;

/**
 * The 22nd (Khara) Drekkana from the candidate Ascendant: its 0-35 drekkana index, the sign that
 * drekkana maps to (Parashari: 1st part → the sign, 2nd → sign+4, 3rd → sign+8), and that sign's
 * ruler — the Khara Drekkana lord (10_maraka_planets.md).
 */
export function khara_drekkana(asc_longitude: number): { index: number; sign: number; lord: string } {
  const asc_sign = Math.trunc(Math.floor(pymod(asc_longitude, 360.0) / 30));
  const asc_part = Math.min(2, Math.trunc(pymod(asc_longitude, 30.0) / 10.0));
  const index = pymod(asc_sign * 3 + asc_part + 21, 36);
  const sign = pymod(Math.trunc(index / 3) + (index % 3) * 4, 12);
  return { index, sign, lord: SIGN_RULERS[sign] };
}

/** Multiplier (0-1) for how sharply this category times under dasha. */
export function event_timing_reliability(event: Record<string, any>): number {
  return CATEGORY_TIMING_RELIABILITY[String(event["category"] || "").trim().toLowerCase()] ?? 0.6;
}

/** The doc-named natural significators (karakas) for an event's category. */
export function event_karakas(event: Record<string, any>): Set<string> {
  return new Set(CATEGORY_KARAKAS[String(event["category"] || "").trim().toLowerCase()] ?? []);
}

const _DASHA_LEVELS = ["maha", "antar", "pratyantar", "sukshma", "prana"];

/**
 * Bhava(s) an event activates: the category's primary house(s) if known,
 * otherwise an empty list.
 */
export function event_house_set(event: Record<string, any>): number[] {
  const category = String(event["category"] || "").trim().toLowerCase();
  // A flipped event (negative signal on an ordinary category, or an explicit GOOD era answer on
  // a negative-natured one — see _houses_flipped) points at the area's CONTRA houses — the
  // bhavas whose activation means the topic did NOT thrive — instead of its primary houses.
  const houses = _houses_flipped(event) ? CATEGORY_CONTRA_HOUSES[category] : CATEGORY_PRIMARY_HOUSES[category];
  return [...(houses ?? [])];
}

// Professional KP scoring weights. The dasha significator chain is the only component
// anchored to the event's actual DATE, so it must dominate: it discriminates between two
// otherwise-plausible ascendants (e.g. Leo vs Capricorn) by checking which one actually
// has the right periods running when each event occurred. The cuspal/Ascendant sub-lords
// are date-blind plausibility gates (does this chart permit the event at all) and the
// Ruling Planets corroborate timed events.
export const KP_COMPONENT_WEIGHTS: Record<string, number> = {
  asc_sublord: 0.3,
  cusp_sublord: 0.8,
  dasha: 2.5,
  ruling_planets: 0.5,
  divisional: 1.4,
  transit: 0.4,
  // Special-lagna confirmations (Vol_01 §3.3 / 01_ascendant_lagna.md). One sign per 24 min
  // (Ghati) / 60 min (Hora), so they discriminate WITHIN a rising sign; light weight per
  // PLAN §6 (secondary checks, corroborate only — 50–100, never penalise). Distinct component
  // keys so a category served by both never collides in the detail record; the weight VALUE is
  // the old shared special_lagna 0.3, so scoring is unchanged when only one fires (the category
  // sets are disjoint, so at most one ever does).
  special_lagna_ghati: 0.3,
  special_lagna_hora: 0.3,
  // Bhava-Lagna generalisation (01_sensitive_points_timing.md "Special Lagnas": "prepare a
  // separate Bhava Kundali for EACH special Lagna and analyze it exactly as the natal Lagna
  // chart") — every category not governed by the Ghati (status/fame) or Hora (wealth) lagna gets
  // the same light KP check against the Bhava Lagna's house structure. LOWER weight than its
  // siblings: the BL advances one sign per 120 min — the coarsest of the three — so its
  // within-sign resolution is weakest and it must corroborate the least.
  special_lagna_bhava: 0.15,
  divisional_fine: 0.9,
  yoga: 0.4,
};
// How the Ruling-Planet component for a clock-timed event splits between the classical KP
// AGREEMENT test (the candidate birth-ascendant's sign/star/sub lords appearing among the event
// moment's RP — the membership rule KP uses for birth-time rectification) and the natal-significator
// STRENGTH of the RP lords. Module-level so lib/tuning can sweep it against the timed benchmark.
export let RP_AGREEMENT_SHARE = 0.75;

/** Tuning seam mirroring Python's module-attribute patching of RP_AGREEMENT_SHARE. */
export function set_rp_agreement_share(value: number): void {
  RP_AGREEMENT_SHARE = value;
}

// Graded membership: the sub-lord is the sharpest (flips every few minutes), star ~50 min, sign ~2 h.
const _RP_SIGN_SHARE = 0.2;
const _RP_STAR_SHARE = 0.3;
const _RP_SUB_SHARE = 0.5;
// When an event's LOCATION is unknown we cast it at the birthplace. The event Ascendant (and thus
// the location-dependent RP AGREEMENT test) is then assumed, not observed, so the Ruling-Planet
// component is down-weighted to this fraction and its agreement reading dropped — it must not
// fabricate strong "exact-second" evidence from a guessed place (e.g. a child born in another city).
const RP_ASSUMED_LOCATION_WEIGHT = 0.5;
// The Shashtiamsa (D60) lagna changes roughly every 2 minutes, so it is the finest classical
// rectification chart — but only meaningful once the time is already narrowed (else it just
// aliases). It is therefore applied as an extra component only when the search window is tight.
export const FINE_DIVISION = 60;

// Logistic scale for mapping signed significator strength -> 0..100. A net of ~+1 grade
// already lifts a lord above neutral; ~+6 saturates near 100. Tuned so a lord that leans
// clearly toward the event's positive houses wins decisively over a neutral/contra one.
const _NET_SCORE_SCALE = 2.5;

// Bonus added to a category's natural karaka(s) net strength (e.g. Venus for marriage):
// being the doc-named karaka AND a chart significator is the strongest endorsement.
const _KARAKA_NET_BONUS = 2.0;

/**
 * Map a lord's significator strength for the event to 0..100, RELATIVE to the other
 * grahas in the same chart (mean-centred), via a logistic curve.
 *
 * Centring on the chart's own mean net is what prevents cross-ascendant saturation bias:
 * when a stellium falls in the event's houses, almost every graha signifies them and the
 * raw net is high for all — which would wrongly inflate that ascendant's score. Scoring
 * each lord against the chart's average means a lord only scores high when it signifies
 * the event houses MORE than the typical graha in that chart, so a saturated chart cannot
 * win on sheer significator mass. Above-average -> ~100, average -> 50, below -> ~0.
 */
function _lord_net_score(
  net: Record<string, number>,
  lord: string | null | undefined,
  scale: number = _NET_SCORE_SCALE,
): number {
  if (!lord) {
    return 50.0;
  }
  const values = Object.values(net);
  const mean = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0.0;
  return 100.0 / (1.0 + Math.exp(-((net[lord] ?? 0.0) - mean) / scale));
}

/**
 * Graded dasha-chain score: each active level's lord is scored by its signed
 * significator strength, weighted by level. Deeper levels (pratyantar -> sukshma) move
 * by the day as birth time shifts the period boundaries, giving fine resolution.
 * Unlike the binary significator-fraction, this stays sharp under a large significator
 * set because contra houses are netted out.
 *
 * deepest_level caps how fine we go: for an untimed event the prana level (hours)
 * depends on the unknown clock time of the event and is pure noise, so callers cap at
 * sukshma; timed events can use prana for second-level resolution.
 *
 * When natal_chart is supplied, each level lord's score is gated by that lord's planetary
 * strength (Shadbala + dignity + combustion): a weak dasha lord cannot deliver what it
 * signifies, so its score is pulled toward neutral (Vol_05; Vol_09 §11.3).
 *
 * fine_level_boost (narrow-window regime only — see FINE_REGIME_BOOST): scales the sukshma+prana
 * level shares ×2 inside this weighted mean, because at second resolution they are the only
 * levels whose boundaries move. The mean renormalises, so the scale stays comparable; the
 * default (false) is byte-identical to the historical behaviour.
 */
export function dasha_net_significator_score(
  event_dt: PlainDateTime,
  birth_dt: PlainDateTime,
  dasha_info_init: Record<string, any>,
  timezone_offset: number,
  net: Record<string, number>,
  requested_level = "maha",
  deepest_level = "sukshma",
  natal_chart: RawChartData | null = null,
  fine_level_boost = false,
): [number | null, Record<string, any>] {
  if (!(requested_level in DASHA_LEVEL_WEIGHTS)) {
    requested_level = "maha";
  }
  let dasha;
  try {
    dasha = get_current_dasha(event_dt, birth_dt, dasha_info_init, timezone_offset);
  } catch {
    return [null, { error: "dasha_error" }];
  }
  const last_index = _DASHA_LEVELS.includes(deepest_level)
    ? _DASHA_LEVELS.indexOf(deepest_level)
    : _DASHA_LEVELS.length - 1;
  const components: Array<[number, number]> = [];
  const detail: Record<string, any> = {};
  for (const level of _DASHA_LEVELS.slice(_DASHA_LEVELS.indexOf(requested_level), last_index + 1)) {
    const lord = dasha_lord_at_level(dasha, level);
    if (lord === null || lord === undefined) {
      continue;
    }
    let level_score = _lord_net_score(net, lord);
    const level_detail: Record<string, any> = { lord: lord, score: pythonRound(level_score, 1) };
    if (natal_chart !== null) {
      const strength = graha_strength_factor(natal_chart, lord);
      level_score = gate_lord_score(natal_chart, lord, level_score);
      level_detail["strength_factor"] = pythonRound(strength, 3);
      level_detail["gated_score"] = pythonRound(level_score, 1);
    }
    const level_weight =
      DASHA_LEVEL_WEIGHTS[level] *
      (fine_level_boost && _FINE_SENSITIVE_LEVELS.has(level) ? FINE_REGIME_BOOST : 1.0);
    components.push([level_score, level_weight]);
    detail[level] = level_detail;
  }
  if (!components.length) {
    return [null, detail];
  }
  return [weighted_mean(components), detail];
}

// ── Generic special-lagna house check (01_sensitive_points_timing.md "Special Lagnas") ───────
// "Keeping the natal planetary positions fixed, prepare a separate Bhava Kundali for EACH special
// Lagna and analyze it exactly as the natal Lagna chart." The Ghati (status) and Hora (wealth)
// checks live in special_points.ts; this module-local generalisation applies the SAME
// occupant-100 / house-lord-85 / neutral-50 grading against ANY special-lagna longitude so the
// remaining categories can be judged from the Bhava Lagna (general life-area house structure,
// 1 sign per 120 min). Lives here (not special_points.ts) because it is scoring wiring, and the
// grading must stay in lock-step with the component weights above.

/**
 * 50–100 confirmation: does a running dasha lord OCCUPY (100) or LORD (85) one of the event's
 * houses counted from the given special-lagna longitude? Neutral 50 otherwise; null when inputs
 * are missing — no component, never a penalty (PLAN §6).
 */
function _score_special_lagna_houses(
  natal_chart: RawChartData,
  lagna_longitude: number,
  houses: number[],
  dasha_lords: Array<string | null | undefined>,
): [number | null, Record<string, any> | null] {
  if (!houses.length) return [null, null];
  const lagna_sign = Math.trunc(Math.floor(pymod(lagna_longitude, 360.0) / 30));
  const lords = dasha_lords.filter((l): l is string => !!l).map((l) => l.toLowerCase());
  if (!lords.length) return [null, null];

  const house_signs = houses.map((h) => pymod(lagna_sign + h - 1, 12));
  let best = 50.0;
  const hits: Record<string, string> = {};
  for (const lord of lords) {
    const planet = natal_chart.planets[lord];
    if (
      planet !== undefined &&
      house_signs.includes(Math.trunc(Math.floor(pymod(Number(planet.longitude), 360.0) / 30)))
    ) {
      best = Math.max(best, 100.0);
      hits[lord] = "occupies";
      continue;
    }
    if (house_signs.some((s) => SIGN_RULERS[s] === lord)) {
      best = Math.max(best, 85.0);
      if (!(lord in hits)) hits[lord] = "lords";
    }
  }
  return [
    best,
    {
      lagna_sign: SIGN_NAMES[lagna_sign],
      houses_from_lagna: houses,
      dasha_lords: lords,
      hits,
      score: pythonRound(best, 1),
    },
  ];
}

/**
 * Always-on professional birth-time scoring, independent of AI expectations.
 *
 * For each dated event it computes the SIGNED KP significator strength of every graha
 * (grade for the event's positive houses minus its contra houses), then rewards birth
 * times where the primary cusp's sub-lord, the running dasha chain, the Ascendant
 * sub-lord, and (for timed events) the Ruling Planets lean toward the positive
 * significators. Netting out the contra houses is what makes these signals
 * discriminate: under the plain four-fold scheme almost every graha qualifies, so
 * nothing distinguishes one birth time from another.
 */
export function score_kp_for_candidate(
  natal_chart: RawChartData,
  birth_dt: PlainDateTime,
  birth_info: Record<string, any>,
  events: Array<Record<string, any>>,
  timezone_offset: number,
  include_fine_divisional = false,
  fine_boost = false,
): [number | null, Array<Record<string, any>>] {
  const scoreable = events.filter((event) => event["start_date"]);
  if (!scoreable.length) {
    return [null, []];
  }

  const asc_chain = kp_sublord_chain(natal_chart.ascendant_longitude);
  const cusps = natal_chart.placidus_cusps;
  let dasha_init: Record<string, number> | null = null;
  const weighted_event_scores: Array<[number, number]> = [];
  const details: Array<Record<string, any>> = [];

  // Ghati + Hora Lagnas for THIS candidate moment (Vol_01 §3.3 / 01_ascendant_lagna.md) —
  // sunrise-anchored and advancing one sign per 24 min (Ghati) / 60 min (Hora), so they separate
  // birth times within a single rising sign. Computed once per candidate (sunrise itself is
  // memoised per date/place).
  const sp_anchor = sunrise_anchor(
    birth_dt, Number(birth_info["lat"]), Number(birth_info["lon"]), timezone_offset,
  );
  const sp_lagnas = sp_anchor.available
    ? special_lagnas(sp_anchor.sun_at_sunrise!, sp_anchor.seconds_since_sunrise!)
    : null;
  const ghati_lagna = sp_lagnas?.ghati_lagna ?? null;
  const hora_lagna = sp_lagnas?.hora_lagna ?? null;
  const bhava_lagna = sp_lagnas?.bhava_lagna ?? null;

  for (const event of scoreable) {
    // "Timed" means a PARSEABLE clock time on an effectively DAY-EXACT date (span ≤ 1 day AND
    // date_accuracy_days ≤ 1 — scoring_common._event_datetime_range). A garbage value like
    // "morning" must degrade to a date-only event, and a real clock riding on a multi-day RANGE
    // pins no moment — neither may earn prana depth, a fabricated Ruling-Planet chart, or the
    // 4× exact-time weight; such events score at the usual midpoint.
    const [range_start_dt, range_end_dt, has_clock] = _event_datetime_range(event);
    const event_dt = event_midpoint_datetime(event);
    // An event dated on/before the birth moment is impossible, and a FUTURE event hasn't
    // happened — either would produce a meaningless dasha verdict, so skip rather than
    // score garbage. (Intake validates new events; this catches legacy/imported data, and
    // run_internal_matching logs the exclusion so the skip is never silent.)
    if (diffSeconds(event_dt, birth_dt) <= 0 || compareDates(dateOnly(event_dt), todayLocal()) > 0) {
      continue;
    }
    // The UTC offset in force where and when the EVENT happened. This differs from the
    // birth offset for DST countries (a January birth vs a July event) and for events at
    // another location; using the birth offset for event-side conversions shifts the
    // event chart/dasha by up to hours — invisible on IST data, wrong everywhere else.
    const event_offset = event_offset_at(event, event_dt, birth_info, timezone_offset);
    const category = String(event["category"] || "").trim().toLowerCase();
    const houses = event_house_set(event);
    if (!houses.length) {
      continue;
    }
    // death_family 3rd house — conditional per the source's own Exception clause
    // (04_houses_1_to_6_significations.md H3: "confirmed only when the 4th house is also
    // afflicted"). Applies whenever the PRIMARY death set is being judged (i.e. the houses are
    // not flipped to contra — which since the negative-natured XOR includes hard era answers).
    if (
      String(event["category"] || "").trim().toLowerCase() === "death_family" &&
      !_houses_flipped(event) && !houses.includes(3) && fourth_house_afflicted(natal_chart)
    ) {
      houses.push(3);
    }
    const contra = event_contra_houses(event);
    const net = net_significator_strength(natal_chart, houses, contra);
    for (const karaka of event_karakas(event)) {
      if (karaka in net) {
        net[karaka] += _KARAKA_NET_BONUS;
      }
    }
    // 22nd (Khara) Drekkana lord joins the death significators at PARTIAL grade
    // (10_maraka_planets.md "Tertiary / Special Marakas"; "effective across all life classes").
    // Only when the PRIMARY death houses are judged — a flipped era answer judges the contra set,
    // where a maraka endorsement would be backwards. Ascendant-drekkana-dependent (~40-min flips),
    // so it discriminates WITHIN a rising sign.
    let khara_detail: Record<string, any> | null = null;
    if (category === "death_family" && !_houses_flipped(event)) {
      const khara = khara_drekkana(natal_chart.ascendant_longitude);
      if (khara.lord in net) net[khara.lord] += KHARA_DREKKANA_NET_BONUS;
      khara_detail = {
        index: khara.index,
        sign: SIGN_NAMES[khara.sign],
        lord: khara.lord,
        net_bonus: KHARA_DREKKANA_NET_BONUS,
      };
    }
    // Net strength must vary across grahas, else the chart cannot discriminate.
    if (!Object.values(net).some((v) => Math.abs(v) > 1e-9)) {
      continue;
    }

    const components: Array<[number, number]> = [];
    const component_detail: Record<string, any> = {
      houses: houses,
      contra_houses: contra,
      net_strength: Object.fromEntries(
        Object.entries(net)
          .sort((a, b) => b[1] - a[1])
          .map(([k, v]) => [k, pythonRound(v, 2)]),
      ),
    };
    if (khara_detail) component_detail["khara_drekkana"] = khara_detail;

    // Sub-lord components are gated by the sub-lord's own strength: a debilitated/combust
    // sub-lord cannot deliver the houses it signifies, so its score is pulled toward neutral.
    const asc_raw = _lord_net_score(net, asc_chain["sub_lord"]);
    const asc_score = gate_lord_score(natal_chart, asc_chain["sub_lord"], asc_raw);
    components.push([asc_score, KP_COMPONENT_WEIGHTS["asc_sublord"]]);
    component_detail["asc_sublord"] = {
      chain: asc_chain,
      score: pythonRound(asc_score, 1),
      strength_factor: pythonRound(graha_strength_factor(natal_chart, asc_chain["sub_lord"]), 3),
    };

    if (cusps && cusps.length >= 12) {
      const cusp_chain = kp_sublord_chain(cusps[houses[0] - 1]);
      const cusp_raw = _lord_net_score(net, cusp_chain["sub_lord"]);
      const cusp_score = gate_lord_score(natal_chart, cusp_chain["sub_lord"], cusp_raw);
      components.push([cusp_score, KP_COMPONENT_WEIGHTS["cusp_sublord"]]);
      component_detail["cusp_sublord"] = {
        house: houses[0],
        chain: cusp_chain,
        score: pythonRound(cusp_score, 1),
        strength_factor: pythonRound(graha_strength_factor(natal_chart, cusp_chain["sub_lord"]), 3),
      };
    }

    if (dasha_init === null) {
      dasha_init = init_dasha_jd(natal_chart, local_to_jd(birth_dt, timezone_offset));
    }
    // Prana (hour resolution) is only meaningful when we know the event's clock time.
    const deepest = has_clock ? "prana" : "sukshma";
    // The drill-down regime boosts the second-sensitive sukshma+prana level shares (see
    // FINE_REGIME_BOOST / FINE_BOOST_MAX_SPAN_SECONDS) — wider passes are byte-identical.
    const [dasha_score_raw, dasha_detail] = dasha_net_significator_score(
      event_dt, birth_dt, dasha_init, event_offset, net, "maha", deepest, natal_chart,
      fine_boost,
    );
    if (dasha_score_raw !== null) {
      // Vol_09 §11.3 synthesis: a Mahadasha lord sitting in a high-Sarvashtakavarga sign
      // delivers the chart's promises in full; a low-SAV sign disappoints. Gate the dasha
      // component by the maha lord's natal-sign SAV.
      const maha_lord = (dasha_detail["maha"] ?? {})["lord"];
      const [sav_factor, sav_detail] = dasha_lord_sav_multiplier(natal_chart, maha_lord);
      // Dasha-Chidra + Decanate timing confirmations (08_dasha_fundamentals.md — see
      // dasha_timing_bonuses): bounded ADDITIVE bonuses on the dasha component, applied after
      // the SAV gate so a junction/decanate hit is never damped away; clamped to 100 and never
      // negative, so a non-matching event stays exactly where it was (never penalised).
      const [timing_bonus, timing_detail] = dasha_timing_bonuses(
        event_dt, birth_dt, dasha_init, event_offset, natal_chart, category,
      );
      const dasha_score = Math.max(
        0.0, Math.min(100.0, 50.0 + (dasha_score_raw - 50.0) * sav_factor + timing_bonus),
      );
      components.push([dasha_score, KP_COMPONENT_WEIGHTS["dasha"]]);
      component_detail["dasha"] = {
        score: pythonRound(dasha_score, 1),
        levels: dasha_detail,
        timing: timing_detail,
        dasha_lord_sav: Object.keys(sav_detail).length
          ? { ...sav_detail, factor: pythonRound(sav_factor, 3) }
          : null,
      };
    }

    // Independent classical cross-check: judge the event in its governing divisional
    // chart (marriage->D9, children->D7, career->D10, education->D24). The dasha lord
    // must signify the event houses in the varga too. This is a separate verdict from
    // the D1 and, because varga lagnas move fast, sharply birth-time sensitive — it is
    // what lets the rising sign converge when the D1 alone is ambiguous between signs.
    const division = category_division(event["category"] ?? "");
    if (division && division !== 1) {
      const [div_score, div_detail] = score_divisional_for_event(
        natal_chart, division, houses, event_dt, birth_dt, dasha_init, event_offset,
      );
      if (div_score !== null) {
        components.push([div_score, KP_COMPONENT_WEIGHTS["divisional"]]);
        component_detail["divisional"] = { score: pythonRound(div_score, 1), ...div_detail };
      }
    }

    // D60 fine-refinement: only once the window is narrow (the caller sets this), because the
    // Shashtiamsa lagna turns over every ~2 min and would only add noise to a wide search.
    if (include_fine_divisional) {
      const [fine_score, fine_detail] = score_divisional_for_event(
        natal_chart, FINE_DIVISION, houses, event_dt, birth_dt, dasha_init, event_offset,
      );
      if (fine_score !== null) {
        // Drill-down regime only: the D60 lagna (~2 min) is one of the few second-sensitive
        // components, so its share is boosted ×FINE_REGIME_BOOST inside FINE_BOOST_MAX_SPAN_SECONDS.
        components.push([
          fine_score,
          KP_COMPONENT_WEIGHTS["divisional_fine"] * (fine_boost ? FINE_REGIME_BOOST : 1.0),
        ]);
        component_detail["divisional_fine"] = { score: pythonRound(fine_score, 1), ...fine_detail };
      }
    }

    // Gochar confirmation: were the slow transiting grahas (Jupiter/Saturn) and the Mahadasha
    // lord actually transiting/aspecting the event's natal houses on the event date? Independent
    // of the dasha-significator and divisional votes, and birth-time sensitive via the houses.
    const transit_lat = optional_float(event["event_lat"]) ?? Number(birth_info["lat"]);
    const transit_lon = optional_float(event["event_lon"]) ?? Number(birth_info["lon"]);
    const [transit_score, transit_detail] = score_transit_for_event(
      natal_chart, houses, event_dt, birth_dt, dasha_init, event_offset, transit_lat, transit_lon,
    );
    if (transit_score !== null) {
      components.push([transit_score, KP_COMPONENT_WEIGHTS["transit"]]);
      component_detail["transit"] = { score: pythonRound(transit_score, 1), ...transit_detail };
    }

    // Yoga activation (Vol_07 §1.2/§2.1/§3.1): for success/wealth events, is the relevant Raja/
    // Dhana yoga formed under THIS lagna, strong, and activated by the dasha at the event date?
    // Formation ← rising sign, activation ← dasha, so it discriminates birth time. Confirmation
    // only (50–100); a candidate that doesn't form the yoga gets no yoga component, not a penalty.
    const [yoga_score, yoga_detail] = score_yoga_for_event(
      natal_chart, String(event["category"] || "").trim().toLowerCase(),
      event_dt, birth_dt, dasha_init, event_offset,
    );
    if (yoga_score !== null) {
      components.push([yoga_score, KP_COMPONENT_WEIGHTS["yoga"]]);
      component_detail["yoga"] = { score: pythonRound(yoga_score, 1), ...yoga_detail };
    }

    // Ghati-Lagna confirmation (Vol_01 §3.3): power/status/fame events are re-judged from the
    // Ghati Lagna — do the running dasha lords activate the event houses in that chart?
    // Secondary check per PLAN §6: 50–100 confirmation, never a penalty, light weight.
    if (ghati_lagna !== null) {
      const [gl_score, gl_detail] = score_ghati_lagna_for_event(
        natal_chart, ghati_lagna, String(event["category"] || ""), houses,
        [(dasha_detail["maha"] ?? {})["lord"], (dasha_detail["antar"] ?? {})["lord"]],
      );
      if (gl_score !== null) {
        components.push([gl_score, KP_COMPONENT_WEIGHTS["special_lagna_ghati"]]);
        component_detail["special_lagna_ghati"] = gl_detail;
      }
    }

    // Hora-Lagna confirmation (01_ascendant_lagna.md — "used exclusively for wealth assessment";
    // "The Dasha of planets placed in Kendras/Trikonas from Hora Lagna correlates with periods of
    // financial gain"): WEALTH events are re-judged from the Hora Lagna. Routed by CATEGORY —
    // HORA_LAGNA_CATEGORIES and GHATI_LAGNA_CATEGORIES are disjoint, so at most one special-lagna
    // check fires per event; the DISTINCT component key keeps its detail record from ever
    // overwriting the Ghati one (same weight value; 50–100 confirmation, never a penalty).
    if (hora_lagna !== null) {
      const [hl_score, hl_detail] = score_hora_lagna_for_event(
        natal_chart, hora_lagna, String(event["category"] || ""),
        [(dasha_detail["maha"] ?? {})["lord"], (dasha_detail["antar"] ?? {})["lord"]],
      );
      if (hl_score !== null) {
        components.push([hl_score, KP_COMPONENT_WEIGHTS["special_lagna_hora"]]);
        component_detail["special_lagna_hora"] = hl_detail;
      }
    }

    // Bhava-Lagna confirmation (01_sensitive_points_timing.md "Special Lagnas": "prepare a
    // separate Bhava Kundali for EACH special Lagna and analyze it exactly as the natal Lagna
    // chart"). Every category NOT already served by its topical special lagna — Ghati
    // (status/fame) or Hora (wealth) — is re-judged from the Bhava Lagna's general life-area
    // house structure: do the running dasha lords occupy/lord the event's houses counted from
    // the BL? The three category routes are disjoint, so at most ONE special-lagna component
    // fires per event. Lightest weight of the three (the BL is the coarsest — 1 sign/120 min);
    // 50–100 confirmation, never a penalty.
    if (
      bhava_lagna !== null &&
      !GHATI_LAGNA_CATEGORIES.has(category) &&
      !HORA_LAGNA_CATEGORIES.has(category)
    ) {
      const [bl_score, bl_detail] = _score_special_lagna_houses(
        natal_chart, bhava_lagna, houses,
        [(dasha_detail["maha"] ?? {})["lord"], (dasha_detail["antar"] ?? {})["lord"]],
      );
      if (bl_score !== null) {
        components.push([bl_score, KP_COMPONENT_WEIGHTS["special_lagna_bhava"]]);
        component_detail["special_lagna_bhava"] = bl_detail;
      }
    }

    if (has_clock) {
      // Use the event's own coordinates when given; otherwise fall back to the birthplace but FLAG
      // it (`?? `, not `||`, so a legitimate 0.0 lat/lon is not treated as missing).
      const raw_event_lat = optional_float(event["event_lat"]);
      const raw_event_lon = optional_float(event["event_lon"]);
      const location_assumed = raw_event_lat === null || raw_event_lon === null;
      const event_lat = raw_event_lat ?? Number(birth_info["lat"]);
      const event_lon = raw_event_lon ?? Number(birth_info["lon"]);
      const event_chart = _cached_event_chart(event_dt, event_lat, event_lon, event_offset);
      const rp = ruling_planets(
        event_chart.ascendant_longitude, event_chart.planets["moon"].longitude, event_dt,
        event_lat, event_lon, event_offset,
      );
      // Two complementary RP readings, blended by RP_AGREEMENT_SHARE:
      // • AGREEMENT — the classical KP rectification test: the BIRTH ascendant's sign/star/
      //   sub lords should appear among the event moment's Ruling Planets. Membership is
      //   graded (sub-lord sharpest — it flips every few minutes, so it is the only
      //   minute-resolution discriminator in this component; star ~50 min; sign ~2 h) and
      //   soft, because both the recalled event time and the candidate grid carry a few
      //   minutes of slop — a hard sub-lord gate would punish the true minute for its
      //   neighbour's match.
      // • STRENGTH — do the event's RP lords signify the event's houses in the natal chart
      //   (gated by their delivery strength)? Meaningful, but measured to vary only slowly
      //   across candidate times, so alone it cannot move the peak (the 2026-06-10 sweep:
      //   every multiplier × weight setting left the wide case at the same 58.55 min).
      const agreement =
        _RP_SIGN_SHARE * (rp.has(asc_chain["sign_lord"]) ? 1 : 0) +
        _RP_STAR_SHARE * (rp.has(asc_chain["star_lord"]) ? 1 : 0) +
        _RP_SUB_SHARE * (rp.has(asc_chain["sub_lord"]) ? 1 : 0);
      const agreement_score = 100.0 * agreement;
      let strength_score = 50.0;
      let first = true;
      for (const lord of rp) {
        const s = gate_lord_score(natal_chart, lord, _lord_net_score(net, lord));
        if (first || s > strength_score) {
          strength_score = s;
          first = false;
        }
      }
      // With the location ASSUMED, the agreement (event-Ascendant RP membership) reading is not
      // trustworthy — keep only the location-robust strength reading and down-weight the whole
      // component, so a guessed place cannot manufacture ruling-planet evidence.
      const rp_score = location_assumed
        ? strength_score
        : RP_AGREEMENT_SHARE * agreement_score + (1.0 - RP_AGREEMENT_SHARE) * strength_score;
      // Drill-down regime: the RP sub-lord membership is the component's minute-resolution
      // discriminator, so its share is boosted ×FINE_REGIME_BOOST inside the boost span.
      const rp_weight =
        KP_COMPONENT_WEIGHTS["ruling_planets"] *
        (location_assumed ? RP_ASSUMED_LOCATION_WEIGHT : 1.0) *
        (fine_boost ? FINE_REGIME_BOOST : 1.0);
      components.push([rp_score, rp_weight]);
      component_detail["ruling_planets"] = {
        rp: [...rp].sort(),
        score: pythonRound(rp_score, 1),
        agreement: pythonRound(agreement_score, 1),
        strength: pythonRound(strength_score, 1),
        location_assumed_birthplace: location_assumed,
        asc_chain_in_rp: {
          sign: rp.has(asc_chain["sign_lord"]),
          star: rp.has(asc_chain["star_lord"]),
          sub: rp.has(asc_chain["sub_lord"]),
        },
      };
    }

    if (!components.length) {
      continue;
    }
    let event_score = weighted_mean(components);
    // Ashtakavarga confirmation (Vol_09 §11.1): scale the event by its primary house's
    // Sarvashtakavarga relative to the chart mean — a strong house can deliver its event,
    // a weak one damps it. Gentle, asc-dependent, so it separates candidate ascendants.
    const [house_sav_factor, house_sav_detail] = event_house_sav_multiplier(natal_chart, houses[0]);
    event_score = Math.max(0.0, Math.min(100.0, 50.0 + (event_score - 50.0) * house_sav_factor));
    component_detail["house_ashtakavarga"] = {
      ...house_sav_detail,
      factor: pythonRound(house_sav_factor, 3),
    };
    // Presence-checked (null/undefined/"" → default 1.0): an EXPLICIT confidence 0 or weight 0
    // must genuinely zero the event, not silently revert to full trust (`|| 1.0` treated 0 as unset).
    const confidence = Math.max(0.0, Math.min(1.0, optional_float(event["confidence"]) ?? 1.0));
    // Effective date accuracy honours the start→end SPAN too (mirrors event_payload_to_life_event):
    // "sometime in 2005" is a fuzzy year even when date_accuracy_days is left at 0, so it must take
    // the fuzzy multiplier, never the ×3.0 day-exact one.
    const date_accuracy = Math.max(
      optional_float(event["date_accuracy_days"]) ?? 0.0,
      Math.abs(diffSeconds(range_end_dt, range_start_dt)) / 86400.0 / 2.0,
    );
    const precision = event_precision_weight_multiplier(date_accuracy, has_clock);
    const reliability = event_timing_reliability(event);
    // Validated severity (0..5, intake-clamped; see event_intake) scales the weight MODESTLY —
    // 0.5 (trivial) .. 1.0 (life-defining), never a veto. Absent severity keeps the full 1.0.
    const raw_severity = optional_float(event["severity"]);
    const severity_factor =
      raw_severity === null ? 1.0 : 0.5 + 0.5 * (Math.max(0.0, Math.min(5.0, raw_severity)) / 5.0);
    // Per-item INFLUENCE (minimal/low/medium/high → 0.1/0.25/0.5/1.0; default high): the user dials
    // how much this event counts toward the events score. Pull its score toward the neutral 50 by
    // the factor (same damping shape as the Ashtakavarga step above) so a lowered event
    // discriminates the signs proportionally less — visible even when it's the only event (a
    // weighted mean of a single value would otherwise ignore a weight multiplier).
    const influence = influence_factor(event["influence"]);
    event_score = Math.max(0.0, Math.min(100.0, 50.0 + (event_score - 50.0) * influence));
    // Influence also scales the WEIGHT: a dialed-down event must stop diluting the OTHER events'
    // discrimination in the weighted mean, not just flatten its own score.
    const event_weight =
      Math.max(0.0, optional_float(event["weight"]) ?? 1.0) *
      confidence * precision * reliability * severity_factor * influence;
    weighted_event_scores.push([event_score, event_weight]);
    details.push({
      date: event["start_date"],
      label: "label" in event ? event["label"] : "",
      category: "category" in event ? event["category"] : "",
      score: pythonRound(event_score, 3),
      effective_weight: pythonRound(event_weight, 3),
      components: component_detail,
    });
  }

  if (!weighted_event_scores.length) {
    return [null, details];
  }
  // Every event weight 0 (e.g. explicit confidence/weight 0 on all events) carries NO event
  // evidence: weighted_mean would return 0.0 by the legacy zero-weight convention, which would
  // read as a hard-fail event fit. Fall back to "no events fit" instead (do not edit
  // legacy_engine.weighted_mean — handled here at the call site).
  if (!weighted_event_scores.some(([, weight]) => weight > 0)) {
    return [null, details];
  }
  return [weighted_mean(weighted_event_scores), details];
}
